import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useTranslation } from "react-i18next";
import {
  getStoreById,
  updateStore,
  uploadStoreLogo,
  uploadStoreCoverImage,
  getMyStoreCategories,
  addStoreCategory,
  setPrimaryStoreCategory,
  removeStoreCategory,
  getStoreKeywords,
  addStoreKeywords,
  deleteStoreKeyword,
} from "../../api/storeService";
import { getStoreCoupons } from "../../api/couponService";
import { searchCities } from "../../api/cityService";
import { getCategories } from "../../api/categoryService";
import { StoreTypeLabels, StoreTypeColors } from "../Admin/constants";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const MAX_COVER_BYTES = 8 * 1024 * 1024;

function couponHeadline(coupon, t) {
  if (coupon.discountType === 3) return t("boStoreProfile:freeDelivery");
  if (coupon.discountType === 1) return t("boStoreProfile:percentOff", { value: coupon.discountValue });
  return t("boStoreProfile:amountOff", { value: coupon.discountValue });
}

export default function StoreProfile() {
  const { t } = useTranslation(["boStoreProfile", "boShared", "common", "boStoreLayoutDesigner"]);
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [coupons, setCoupons] = useState([]);

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  function load() {
    setLoading(true);
    setError("");
    getStoreById(storeId)
      .then(setStore)
      .catch(() => setError(t("boStoreProfile:loadError")))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [storeId]);

  useEffect(() => {
    getStoreCoupons(storeId)
      .then(setCoupons)
      .catch(() => setCoupons([]));
  }, [storeId]);

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t("boShared:invalidImageType"));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t("boStoreProfile:logoTooLarge"));
      return;
    }
    setError("");
    setUploadingLogo(true);
    try {
      const updated = await uploadStoreLogo(storeId, file);
      setStore(updated);
      setNotice(t("boStoreProfile:logoUpdated"));
    } catch {
      setError(t("boStoreProfile:logoUploadError"));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleCoverChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t("boShared:invalidImageType"));
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      setError(t("boStoreProfile:coverTooLarge"));
      return;
    }
    setError("");
    setUploadingCover(true);
    try {
      const updated = await uploadStoreCoverImage(storeId, file);
      setStore(updated);
      setNotice(t("boStoreProfile:coverUpdated"));
    } catch {
      setError(t("boStoreProfile:coverUploadError"));
    } finally {
      setUploadingCover(false);
    }
  }

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={220} sx={{ mb: 2, borderRadius: 3 }} />
        <Skeleton variant="text" width={240} height={36} />
        <Skeleton variant="text" width={160} />
      </Box>
    );
  }

  if (error && !store) {
    return (
      <Box>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/business-owner/stores")} sx={{ mb: 2 }}>
          {t("boStoreProfile:backToStores")}
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const rating = Number(store.rating ?? 0);

  return (
    <Box sx={{ maxWidth: 920 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/business-owner/stores")} sx={{ mb: 2 }}>
        {t("boStoreProfile:backToStores")}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, overflow: "hidden" }}>
        {/* Cover image - mirrors the client-facing store page banner */}
        <Box
          sx={{
            position: "relative",
            height: { xs: 140, sm: 220 },
            background: "linear-gradient(135deg, rgba(22,199,154,0.22), rgba(255,182,39,0.22))",
          }}
        >
          {store.coverImageUrl && (
            <Box
              component="img"
              src={store.coverImageUrl}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}
          <ImageEditOverlay
            label={t("boStoreProfile:changeCover")}
            uploading={uploadingCover}
            onChange={handleCoverChange}
            sx={{ position: "absolute", bottom: 10, right: 10 }}
          />
        </Box>

        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ position: "relative", width: 96, height: 96, mt: "-48px", mb: 1.5 }}>
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: 3,
                p: 0.5,
                bgcolor: "background.paper",
                boxShadow: 3,
              }}
            >
              {store.logoUrl ? (
                <Box
                  component="img"
                  src={store.logoUrl}
                  alt=""
                  sx={{ width: "100%", height: "100%", borderRadius: 2, objectFit: "cover", display: "block" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StorefrontRoundedIcon sx={{ fontSize: 34, color: "text.secondary" }} />
                </Box>
              )}
            </Box>
            <ImageEditOverlay
              label={t("boStoreProfile:changeLogo")}
              uploading={uploadingLogo}
              onChange={handleLogoChange}
              round
              sx={{ position: "absolute", bottom: -4, right: -4 }}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.75 }}>
            <Typography variant="h5" fontWeight={800}>
              {store.storeName}
            </Typography>
            {store.isApproved && <VerifiedRoundedIcon color="primary" fontSize="small" />}
            {store.isPrime && (
              <Chip size="small" color="secondary" icon={<BoltRoundedIcon />} label={t("boStoreProfile:primeBadge")} />
            )}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, rowGap: 1 }}>
            <Chip size="small" label={StoreTypeLabels[store.type] || t("boShared:storeFallback")} color={StoreTypeColors[store.type] || "default"} />
            <Chip size="small" label={store.isActive ? t("boShared:active") : t("boShared:inactive")} color={store.isActive ? "success" : "default"} variant="outlined" />
            {!store.isApproved && <Chip size="small" label={t("boShared:pendingApproval")} color="warning" variant="outlined" />}
            {store.isComingSoon && <Chip size="small" label={t("boShared:comingSoon")} color="info" variant="outlined" />}
          </Stack>

          <Stack direction="row" spacing={3} sx={{ mt: 2 }} flexWrap="wrap" rowGap={1}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StarRoundedIcon sx={{ fontSize: 18, color: "warning.main" }} />
              <Typography variant="body2" fontWeight={700}>
                {rating > 0 ? rating.toFixed(1) : "-"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("boStoreProfile:ratingLabel")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PeopleAltOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={700}>
                {store.followersCount ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("boShared:followers")}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }} flexWrap="wrap" rowGap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewRoundedIcon />}
              onClick={() => window.open(`/products?storeId=${store.storeId}`, "_blank", "noopener")}
            >
              {t("boStoreProfile:viewLiveStore")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<QueryStatsRoundedIcon />}
              onClick={() => navigate(`/business-owner/dashboard?storeId=${store.storeId}`)}
            >
              {t("boShared:analytics")}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIphoneRoundedIcon />}
              onClick={() => navigate(`/business-owner/stores/${store.storeId}/layout`)}
            >
              {t("boStoreLayoutDesigner:title")}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <StoreDetailsForm store={store} onSaved={(updated) => { setStore(updated); setNotice(t("boStoreProfile:detailsUpdated")); }} />

      <StoreCategoriesCard storeId={store.storeId} />

      <StoreKeywordsCard storeId={store.storeId} />

      {coupons.length > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <LocalOfferOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t("boStoreProfile:activeDeals", { count: coupons.length })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
              {coupons.map((coupon) => (
                <Box
                  key={coupon.couponId}
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    minWidth: 180,
                  }}
                >
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {couponHeadline(coupon, t)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("boStoreProfile:couponCode", { code: coupon.code })}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function ImageEditOverlay({ label, uploading, onChange, round, sx }) {
  const inputRef = useRef(null);
  return (
    <Tooltip title={label}>
      <Box sx={sx}>
        <IconButton
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{
            bgcolor: "rgba(15, 23, 42, 0.65)",
            color: "#fff",
            borderRadius: round ? "50%" : 2,
            "&:hover": { bgcolor: "rgba(15, 23, 42, 0.8)" },
          }}
        >
          {uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CameraAltOutlinedIcon fontSize="small" />}
        </IconButton>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={onChange} disabled={uploading} />
      </Box>
    </Tooltip>
  );
}

function StoreDetailsForm({ store, onSaved }) {
  const { t } = useTranslation(["boStoreProfile", "boShared", "common"]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [storeName, setStoreName] = useState(store.storeName || "");
  const [storeNameArabic, setStoreNameArabic] = useState(store.storeNameArabic || "");
  const [description, setDescription] = useState(store.description || "");
  const [addressText, setAddressText] = useState(store.addressText || "");
  const [latitude, setLatitude] = useState(store.latitude ?? "");
  const [longitude, setLongitude] = useState(store.longitude ?? "");
  const [locating, setLocating] = useState(false);
  const [wholesaleMinOrderAmount, setWholesaleMinOrderAmount] = useState(store.wholesaleMinOrderAmount ?? "");
  const [wholesaleMinOrderUnits, setWholesaleMinOrderUnits] = useState(store.wholesaleMinOrderUnits ?? "");

  const [cityInput, setCityInput] = useState(store.cityNameEnglish || store.city || "");
  const [selectedCity, setSelectedCity] = useState(
    store.city ? { nameHebrew: store.city, nameEnglish: store.cityNameEnglish, nameArabic: store.cityNameArabic } : null
  );
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);

  const isWholesale = store.type === 2 || store.type === 3;

  function resetForm() {
    setStoreName(store.storeName || "");
    setStoreNameArabic(store.storeNameArabic || "");
    setDescription(store.description || "");
    setAddressText(store.addressText || "");
    setLatitude(store.latitude ?? "");
    setLongitude(store.longitude ?? "");
    setWholesaleMinOrderAmount(store.wholesaleMinOrderAmount ?? "");
    setWholesaleMinOrderUnits(store.wholesaleMinOrderUnits ?? "");
    setCityInput(store.cityNameEnglish || store.city || "");
    setSelectedCity(store.city ? { nameHebrew: store.city, nameEnglish: store.cityNameEnglish, nameArabic: store.cityNameArabic } : null);
    setError("");
  }

  function handleCancel() {
    resetForm();
    setEditing(false);
  }

  useEffect(() => {
    if (!editing) return;
    const query = cityInput.trim();
    if (!query) {
      setCityOptions([]);
      return;
    }
    let active = true;
    setCityLoading(true);
    const timer = setTimeout(() => {
      searchCities(query, 15)
        .then((results) => {
          if (active) setCityOptions(results);
        })
        .catch(() => {
          if (active) setCityOptions([]);
        })
        .finally(() => {
          if (active) setCityLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityInput, editing]);

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      setError(t("boStoreProfile:geoUnavailable"));
      return;
    }
    setError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setLocating(false);
      },
      () => {
        setError(t("boStoreProfile:geoError"));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  async function handleSave() {
    const trimmedName = storeName.trim();
    if (!trimmedName) {
      setError(t("boStoreProfile:nameRequiredError"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await updateStore(store.storeId, {
        storeName: trimmedName,
        storeNameArabic: storeNameArabic.trim() || null,
        description: description.trim() || null,
        city: selectedCity?.nameHebrew ?? store.city ?? null,
        addressText: addressText.trim() || null,
        latitude: latitude !== "" ? Number(latitude) : null,
        longitude: longitude !== "" ? Number(longitude) : null,
        wholesaleMinOrderAmount: isWholesale && wholesaleMinOrderAmount !== "" ? Number(wholesaleMinOrderAmount) : null,
        wholesaleMinOrderUnits: isWholesale && wholesaleMinOrderUnits !== "" ? Number(wholesaleMinOrderUnits) : null,
        isActive: store.isActive,
      });
      onSaved(updated);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data || t("boStoreProfile:saveError"));
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="subtitle1" fontWeight={700}>
              {t("boStoreProfile:storeDetails")}
            </Typography>
            <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={() => setEditing(true)}>
              {t("common:edit")}
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("boStoreProfile:storeName")}
              </Typography>
              <Typography variant="body2">{store.storeName || "-"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("boStoreProfile:storeNameArabic")}
              </Typography>
              <Typography variant="body2">{store.storeNameArabic || "-"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("boShared:description")}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {store.description || t("boStoreProfile:noDescription")}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("boStoreProfile:city")}
              </Typography>
              <Typography variant="body2">{store.cityNameEnglish || store.city || t("boShared:noCitySet")}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("boStoreProfile:address")}
              </Typography>
              <Typography variant="body2">{store.addressText || "-"}</Typography>
            </Grid>
            {isWholesale && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {t("boStoreProfile:wholesaleMinAmount")}
                  </Typography>
                  <Typography variant="body2">{store.wholesaleMinOrderAmount ?? "-"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {t("boStoreProfile:wholesaleMinUnits")}
                  </Typography>
                  <Typography variant="body2">{store.wholesaleMinOrderUnits ?? "-"}</Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          {t("boStoreProfile:editStoreDetails")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("boStoreProfile:storeName")}
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("boStoreProfile:storeNameArabic")}
              value={storeNameArabic}
              onChange={(e) => setStoreNameArabic(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label={t("boShared:description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
              disabled={saving}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              size="small"
              options={cityOptions}
              loading={cityLoading}
              value={selectedCity}
              inputValue={cityInput}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option?.nameEnglish || option?.nameHebrew || ""}
              isOptionEqualToValue={(option, value) => option.nameHebrew === value?.nameHebrew}
              onInputChange={(_e, value) => setCityInput(value)}
              onChange={(_e, value) => {
                setSelectedCity(value);
                setCityInput(value?.nameEnglish || value?.nameHebrew || "");
              }}
              disabled={saving}
              renderOption={(props, option) => (
                <li {...props} key={option.cityId}>
                  {option.nameEnglish || option.nameHebrew}
                  {option.districtName ? ` · ${option.districtName}` : ""}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label={t("boStoreProfile:city")} />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("boStoreProfile:address")}
              value={addressText}
              onChange={(e) => setAddressText(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid size={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("boStoreProfile:mapLocationOptional")}
              </Typography>
              <Button size="small" startIcon={locating ? <CircularProgress size={14} /> : <MyLocationRoundedIcon fontSize="small" />} onClick={handleUseCurrentLocation} disabled={saving || locating}>
                {t("boStoreProfile:useCurrentLocation")}
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("boStoreProfile:latitude")}
              type="number"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={t("boStoreProfile:longitude")}
              type="number"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          {isWholesale && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("boStoreProfile:wholesaleMinAmount")}
                  type="number"
                  value={wholesaleMinOrderAmount}
                  onChange={(e) => setWholesaleMinOrderAmount(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={saving}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("boStoreProfile:wholesaleMinUnits")}
                  type="number"
                  value={wholesaleMinOrderUnits}
                  onChange={(e) => setWholesaleMinOrderUnits(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={saving}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t("boShared:saving") : t("boStoreProfile:saveChanges")}
          </Button>
          <Button onClick={handleCancel} disabled={saving}>
            {t("common:cancel")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function buildChildrenByParent(categories) {
  const map = new Map();
  categories.forEach((c) => {
    const key = c.parentCategoryId ?? null;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  });
  map.forEach((list) => list.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)));
  return map;
}

function getDescendantIds(categoryId, childrenByParent) {
  const result = [];
  const stack = [...(childrenByParent.get(categoryId) || [])];
  while (stack.length) {
    const c = stack.pop();
    result.push(c.categoryId);
    const kids = childrenByParent.get(c.categoryId);
    if (kids) stack.push(...kids);
  }
  return result;
}

// A category only appears once its parent is linked to the store, mirroring the backend
// rule that subcategories require their parent to be linked first.
function buildSelectableRows(childrenByParent, linkedIds) {
  const rows = [];
  const addLevel = (parentId, depth) => {
    (childrenByParent.get(parentId) || []).forEach((c) => {
      rows.push({ ...c, depth });
      if (linkedIds.has(c.categoryId)) addLevel(c.categoryId, depth + 1);
    });
  };
  addLevel(null, 0);
  return rows;
}

function StoreCategoriesCard({ storeId }) {
  const { t } = useTranslation("boStoreProfile");
  const [allCategories, setAllCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingCategoryId, setSavingCategoryId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getCategories({}), getMyStoreCategories(storeId)])
      .then(([categories, linked]) => {
        if (!active) return;
        setAllCategories(categories.filter((c) => c.isActive));
        setStoreCategories(linked);
      })
      .catch(() => {
        if (active) setError(t("loadCategoriesError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const childrenByParent = useMemo(() => buildChildrenByParent(allCategories), [allCategories]);
  const linkedCategoryIds = useMemo(() => new Set(storeCategories.map((sc) => sc.categoryId)), [storeCategories]);
  const categoryRows = useMemo(() => buildSelectableRows(childrenByParent, linkedCategoryIds), [childrenByParent, linkedCategoryIds]);

  async function removeCategoryLink(categoryId, descendantIds) {
    setSavingCategoryId(categoryId);
    setError("");
    try {
      await removeStoreCategory(storeId, categoryId);
      const removedIds = new Set([categoryId, ...descendantIds]);
      setStoreCategories((prev) => prev.filter((sc) => !removedIds.has(sc.categoryId)));
    } catch {
      setError(t("updateCategoryError"));
    } finally {
      setSavingCategoryId(null);
    }
  }

  async function toggleCategory(category) {
    const existing = storeCategories.find((sc) => sc.categoryId === category.categoryId);

    if (existing) {
      const descendantIds = getDescendantIds(category.categoryId, childrenByParent);
      const hasLinkedDescendants = descendantIds.some((id) => linkedCategoryIds.has(id));
      if (hasLinkedDescendants) {
        const confirmed = window.confirm(t("removeSubcategoriesConfirm"));
        if (!confirmed) return;
      }
      await removeCategoryLink(category.categoryId, descendantIds);
      return;
    }

    setSavingCategoryId(category.categoryId);
    setError("");
    try {
      const updated = await addStoreCategory(storeId, category.categoryId, storeCategories.length === 0);
      setStoreCategories(updated);
    } catch (err) {
      setError(typeof err.response?.data === "string" ? err.response.data : t("updateCategoryError"));
    } finally {
      setSavingCategoryId(null);
    }
  }

  async function makePrimary(categoryId) {
    setSavingCategoryId(categoryId);
    setError("");
    try {
      const updated = await setPrimaryStoreCategory(storeId, categoryId);
      setStoreCategories(updated);
    } catch {
      setError(t("setPrimaryError"));
    } finally {
      setSavingCategoryId(null);
    }
  }

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {t("categoriesTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("categoriesSubtitle")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : categoryRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("noCategories")}
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {categoryRows.map((row) => {
              const linked = storeCategories.find((sc) => sc.categoryId === row.categoryId);
              const isSaving = savingCategoryId === row.categoryId;
              return (
                <Stack
                  key={row.categoryId}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ ml: row.depth * 3, py: 0.75 }}
                >
                  <IconButton size="small" onClick={() => toggleCategory(row)} disabled={isSaving}>
                    {linked ? (
                      <CheckCircleRoundedIcon fontSize="small" color="primary" />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon fontSize="small" color="disabled" />
                    )}
                  </IconButton>
                  <Typography variant="body2" sx={{ flexGrow: 1 }} color={linked ? "primary.main" : "text.primary"}>
                    {row.name}
                  </Typography>
                  {linked && (
                    <IconButton size="small" onClick={() => makePrimary(row.categoryId)} disabled={isSaving}>
                      {linked.isPrimary ? (
                        <StarRoundedIcon fontSize="small" sx={{ color: "warning.main" }} />
                      ) : (
                        <StarOutlineRoundedIcon fontSize="small" color="disabled" />
                      )}
                    </IconButton>
                  )}
                  {isSaving && <CircularProgress size={16} />}
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function StoreKeywordsCard({ storeId }) {
  const { t } = useTranslation(["boStoreProfile", "common"]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    getStoreKeywords(storeId)
      .then((data) => {
        if (active) setKeywords(data);
      })
      .catch(() => {
        if (active) setError(t("boStoreProfile:loadKeywordsError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  async function handleAdd() {
    const trimmed = newKeyword.trim();
    if (!trimmed) return;
    setAdding(true);
    setError("");
    try {
      const updated = await addStoreKeywords(storeId, [trimmed]);
      setKeywords(updated);
      setNewKeyword("");
    } catch {
      setError(t("boStoreProfile:addKeywordError"));
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(keywordId) {
    setDeletingId(keywordId);
    setError("");
    try {
      await deleteStoreKeyword(storeId, keywordId);
      setKeywords((prev) => prev.filter((k) => k.keywordId !== keywordId));
    } catch {
      setError(t("boStoreProfile:removeKeywordError"));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {t("boStoreProfile:keywordsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("boStoreProfile:keywordsSubtitle")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder={t("boStoreProfile:addKeywordPlaceholder")}
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            disabled={adding}
            fullWidth
          />
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={handleAdd} disabled={adding || !newKeyword.trim()}>
            {t("common:add")}
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : keywords.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("boStoreProfile:noKeywords")}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
            {keywords.map((keyword) => (
              <Chip
                key={keyword.keywordId}
                label={keyword.keyword}
                onDelete={deletingId === keyword.keywordId ? undefined : () => handleRemove(keyword.keywordId)}
                deleteIcon={deletingId === keyword.keywordId ? <CircularProgress size={14} /> : <CloseRoundedIcon />}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
